'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'next/navigation';

import {
  addCheckoutProducts,
  transformUserProductToCheckoutProduct,
} from '@/modules/checkout/helpers';
import { guestCheckoutSummaryQueryOptions } from '@/modules/checkout/queries';
import { CheckoutType } from '@/modules/checkout/types';
import { clearCombinedFlowCookie } from '@/modules/experiments/helpers/clearCombinedFlowCookie';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { useCurrentLanguage } from '@/modules/language/useCurrentLanguage';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { useProductBySlugExtendedPDP } from '@/modules/product/hooks/useProductBySlugExtendedPDP';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function useBuyNowAction() {
  const { data: productBasic } = useProductBySlugBasicPDP();
  const { data: productExtended } = useProductBySlugExtendedPDP();
  const { slug } = useParams<{ slug: string }>();
  const { sendBuyNowActionEvents } = useProductTracking();
  const [isAuthenticated] = useCurrentUser();
  const { language } = useCurrentLanguage();
  const queryClient = useQueryClient();

  const [, bucketFn] = useExperiments(['web_8651_buy_now_presignup'], {
    deferred: true,
  });

  function handleBuyNowButtonClick(selectedVariantId?: number | string) {
    if (!productBasic || !productExtended) {
      return;
    }

    if (!isAuthenticated) {
      bucketFn();
    }

    const selectedSize = {
      id: selectedVariantId ? String(selectedVariantId) : '',
      name: productExtended.sizes?.find(
        (size) => size.id === Number(selectedVariantId)
      )?.name,
    };

    const products = [
      transformUserProductToCheckoutProduct(
        {
          ...productBasic,
          ...productExtended,
        },
        slug,
        selectedSize
      ),
    ];

    addCheckoutProducts({
      products,
      type: CheckoutType.SingleProduct,
    });

    if (!isAuthenticated) {
      void queryClient.prefetchQuery(
        guestCheckoutSummaryQueryOptions({ products, language })
      );
    } else {
      void clearCombinedFlowCookie();
    }

    sendBuyNowActionEvents(productBasic, productExtended);
  }

  return { handleBuyNowButtonClick };
}
